import type { Immutable } from "immer";
import React from "react";

/**
 * Inline icon SVG for ``ProductSpecificationsBlock`` (#33410), transcribed
 * VERBATIM from ``static/dist2/svg-django/tooltip-icon.svg`` -- the source the
 * legacy ``{% include 'cms/components/svg.html' with src='tooltip-icon' ... %}``
 * include rendered server-side. Inline (not ``react-inlinesvg``) so the markup
 * is present in the SSR/fragment HTML (the SizeGuide / HardcodedBlock
 * precedent: ``react-inlinesvg`` fetches client-side on mount, so the icon
 * would be ABSENT from SSR HTML).
 *
 * The source ``tooltip-icon.svg`` carries a DUPLICATE ``class`` attribute
 * (``class="{{ class }}"`` then a literal ``class="tooltip__handle"``); HTML5
 * keeps the FIRST, and every partial passes ``class="tooltip__handle"`` anyway,
 * so the resolved class is always ``tooltip__handle``. Size-guide triggers pass
 * ``description`` and ``ariaHidden`` for A360 #350; the optional title/description
 * slots stay for parity with the legacy include.
 */
type SvgElementProps = Immutable<{
    className: string;
    title?: string;
    ariaHidden: boolean;
}>;

type IconComponent = (props: SvgElementProps) => React.JSX.Element;

const Title = (
    props: Immutable<{ title?: string }>,
): React.JSX.Element | null =>
    props.title ? <title>{props.title}</title> : null;

export const TooltipIcon: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 100 100"
    >
        <Title title={props.title} />
        <path d="M50 4.25C24.773 4.25 4.25 24.773 4.25 50S24.773 95.75 50 95.75 95.75 75.227 95.75 50 75.227 4.25 50 4.25m4.705 71.305H44.512v-9.513h10.193zm6.621-25.801c-3.008 2.239-6.223 4.058-6.852 8.181v2.66h-9.439v-3.146c.418-6.086 3.285-8.811 6.082-10.979 2.727-2.166 5.104-3.773 5.104-7.551 0-4.266-2.236-6.293-5.941-6.293-5.033 0-7.131 4.125-7.203 9.09H32.799c.211-9.998 6.783-17.27 16.99-17.27 13.215 0 17.412 8.111 17.412 13.492 0 6.784-2.867 9.511-5.875 11.816" />
    </svg>
);

// Wrapper reproducing ``cms/components/svg.html``: the optional
// ``.ada-screenreader-only`` description span SIBLING BEFORE the inline ``<svg>``.
// Size-guide tooltip triggers pass ``description`` and ``ariaHidden`` (A360 #350).
type SvgProps = Immutable<{
    Icon: IconComponent;
    src: string; // kept for parity/documentation; not rendered
    className?: string;
    title?: string;
    description?: string;
    ariaHidden?: boolean;
}>;

export const Svg = (props: SvgProps): React.JSX.Element => {
    const Icon = props.Icon;
    return (
        <>
            {props.description ? (
                <span className="ada-screenreader-only">
                    {props.description}
                </span>
            ) : null}
            <Icon
                className={props.className ?? ""}
                title={props.title || undefined}
                ariaHidden={Boolean(props.ariaHidden)}
            />
        </>
    );
};
